import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Circle, GoogleMap, InfoWindowF, Marker, useJsApiLoader } from '@react-google-maps/api';

import purplePin from '../../../../assets/icons/MapPurplePin.svg';
import { getAverageCoordinates } from '../../../../utils/locationHelpers';
import googleMapsLoaderConfig from '../../googleMapsLoaderConfig';
import { defaultCenter, defaultZoomLevel } from '../constants';

const containerStyle = {
  width: '100%',
  height: '100%',
};

const GpsMap = ({ markers = [], workLocationMarker }) => {
  const { isLoaded } = useJsApiLoader(googleMapsLoaderConfig);

  const [activeMarker, setActiveMarker] = useState(null);
  const [mapRef, setMapRef] = useState(null);

  const fitMapToBounds = useCallback(() => {
    if (!mapRef || (workLocationMarker ? [...markers, workLocationMarker] : markers).length === 0)
      return;

    const bounds = new window.google.maps.LatLngBounds();
    (workLocationMarker ? [...markers, workLocationMarker] : markers).forEach((location) => {
      bounds.extend({ lat: Number(location.lat), lng: Number(location.lng) });
    });

    mapRef.fitBounds(bounds);
  }, [mapRef, markers, workLocationMarker]);

  const avgCenter = getAverageCoordinates({
    locations: ((workLocationMarker ? [...markers, workLocationMarker] : markers) || []).map(
      (mark) => ({
        target: {
          coordinates: [mark.lng, mark.lat],
        },
      })
    ),
  });

  const onLoad = useCallback((map) => {
    setMapRef(map);
  }, []);

  const handleActiveMarker = (marker) => {
    if (marker === activeMarker) {
      return;
    }
    setActiveMarker(marker);
  };

  useEffect(() => {
    fitMapToBounds();
  }, [fitMapToBounds]);

  const getCenter = () => {
    if (avgCenter && avgCenter.lat && avgCenter.lng) {
      return avgCenter;
    }
    return defaultCenter;
  };
  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={getCenter()}
      zoom={defaultZoomLevel}
      onLoad={onLoad}
      data-testid="google-map"
    >
      {workLocationMarker && (
        <React.Fragment key="jobSite">
          <Marker
            icon={workLocationMarker?.icon || purplePin}
            position={{ lat: workLocationMarker?.lat, lng: workLocationMarker?.lng }}
            onClick={() => handleActiveMarker(workLocationMarker?._id)}
            onUnmount={() => setActiveMarker(null)}
          >
            {workLocationMarker?._id === activeMarker ? (
              <InfoWindowF onCloseClick={() => setActiveMarker(null)}>
                <div>{workLocationMarker?.name}</div>
              </InfoWindowF>
            ) : null}
          </Marker>
          {workLocationMarker?.radius && (
            <Circle
              center={{ lat: workLocationMarker?.lat, lng: workLocationMarker?.lng }}
              radius={workLocationMarker?.radius}
              options={{
                strokeColor: 'transparent',
                fillColor: 'red',
                fillOpacity: 0.15,
              }}
            />
          )}
        </React.Fragment>
      )}
      {markers.map((marker) => (
        <React.Fragment key={marker?._id}>
          <Marker
            icon={marker?.icon || purplePin}
            position={{ lat: marker?.lat, lng: marker?.lng }}
            onClick={() => handleActiveMarker(marker?._id)}
            onUnmount={() => setActiveMarker(null)}
          >
            {marker?._id === activeMarker ? (
              <InfoWindowF onCloseClick={() => setActiveMarker(null)}>
                <div>{marker?.name}</div>
              </InfoWindowF>
            ) : null}
          </Marker>
          {marker?.radius && (
            <Circle
              center={{ lat: marker?.lat, lng: marker?.lng }}
              radius={marker?.radius} // Meters TODO: verify if meters is correct for both sides
              options={{
                strokeColor: marker?.strokeColor || 'transparent',
                fillColor: marker?.fillColor || 'blue',
                fillOpacity: marker?.fillOpacity || 0.35,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </GoogleMap>
  ) : (
    <>CONTENT</>
  );
};

GpsMap.propTypes = {
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
      radius: PropTypes.number,
      name: PropTypes.string,
      checked: PropTypes.bool,
      isSuspicious: PropTypes.bool,
      icon: PropTypes.string,
    })
  ),
  workLocationMarker: PropTypes.shape({
    _id: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number,
    radius: PropTypes.number,
    icon: PropTypes.string,
    name: PropTypes.string,
  }),
};

export default GpsMap;
